<template>
    <form @submit.prevent="handleUpdate" class="setup-form">
        <div class="setup-card">
            <div class="setup-header">
                <div class="setup-avatar">
                    <LoadingSmallDiv v-if="isLoading" />
                    <span v-else>
                        <img :src="user?.profile_img" class="circle responsive-img" v-if="user?.profile_img" />
                        <span v-else>
                            <img :src="img" class="circle responsive-img" v-if="img" />
                            <img :src="`https://ui-avatars.com/api/?name=${user.name}&size=128`"
                                class="circle responsive-img" v-else />
                        </span>
                    </span>
                </div>
                <div class="setup-title">
                    <img src="/img/icons/icon-192x192.png" alt="easyHMO" class="setup-logo" />
                    <h5>Account setup</h5>
                    <p>Confirm your details and set a new password.</p>
                </div>
            </div>

            <div class="file-field input-field setup-upload">
                <div class="btn setup-upload-btn">
                    <span>Photo</span>
                    <input type="file" @change="handleFile" />
                </div>
                <div class="file-path-wrapper">
                    <input class="file-path" v-model="inputFile" type="text" />
                    <p v-if="fileError">{{ fileError }}</p>
                </div>
            </div>

            <div class="setup-grid">
                <div class="input-field">
                    <input type="text" v-model="name" class="validate" required />
                    <label class="active" for="name">*Fullname</label>
                </div>

                <div class="input-field">
                    <input type="text" v-model="slug" class="validate" readonly />
                    <label class="active" for="slug">Slug</label>
                </div>

                <template v-if="user.type === 'hmo' || user.type === 'provider' || user.type === 'client'">
                    <div class="input-field">
                        <input type="text" v-model="staff_id" class="validate" />
                        <label class="active" for="staff_id">Staff ID</label>
                    </div>

                    <div class="input-field">
                        <input type="text" v-model="department" class="validate" required />
                        <label class="active" for="department">*Department</label>
                    </div>

                    <div class="input-field">
                        <input type="text" v-model="designation" class="validate" required />
                        <label class="active" for="designation">*Designation</label>
                    </div>

                    <div class="input-field">
                        <input type="text" v-model="official_line" class="validate" />
                        <label class="active" for="official_line">Official Line</label>
                    </div>
                </template>
            </div>

            <div class="password-section">
                <h6>Change Password</h6>
                <div class="setup-grid">
                    <div class="input-field">
                        <input type="email" v-model="email" class="validate" readonly />
                        <label class="active" for="email">Email</label>
                    </div>
                    <div class="input-field">
                        <input type="password" autocomplete="new-password" v-model="password" class="validate" required />
                        <label class="active" for="password">*New Password</label>
                    </div>

                    <div class="input-field">
                        <input type="password" autocomplete="new-password" v-model="password_confirm" class="validate" required />
                        <label class="active" for="password_confirm">*Confirm Password</label>
                    </div>
                </div>

                <transition name="setup-notice-slide">
                    <div class="password-guidance" v-if="shouldShowPasswordNotice">
                        <p>Choose a password that meets these requirements.</p>
                        <ul>
                            <li :class="{ met: passwordRules.hasLength }">
                                <i class="material-icons">{{ passwordRules.hasLength ? "check_circle" : "radio_button_unchecked" }}</i>
                                <span>8 or more characters</span>
                            </li>
                            <li :class="{ met: passwordRules.hasLetterOrSymbol }">
                                <i class="material-icons">{{ passwordRules.hasLetterOrSymbol ? "check_circle" : "radio_button_unchecked" }}</i>
                                <span>At least 1 letter or symbol</span>
                            </li>
                            <li :class="{ met: passwordRules.hasNumber }">
                                <i class="material-icons">{{ passwordRules.hasNumber ? "check_circle" : "radio_button_unchecked" }}</i>
                                <span>At least 1 number</span>
                            </li>
                            <li :class="{ met: passwordRules.isDifferentFromTemporary }" v-if="hasTemporaryPassword">
                                <i class="material-icons">{{ passwordRules.isDifferentFromTemporary ? "check_circle" : "radio_button_unchecked" }}</i>
                                <span>Different from the temporary password</span>
                            </li>
                            <li :class="{ met: passwordRules.confirmMatches }">
                                <i class="material-icons">{{ passwordRules.confirmMatches ? "check_circle" : "radio_button_unchecked" }}</i>
                                <span>Confirmation matches</span>
                            </li>
                        </ul>
                    </div>
                </transition>
            </div>

            <div class="setup-actions">
                <span class="setup-error" v-if="error">{{ error }}</span>

                <button class="waves-effect waves-light btn-large setup-submit" :class="{
                    disabled: isSubmitting || isLoading,
                }" :disabled="isSubmitting || isLoading">
                    <span class="setup-submit-content">
                        <span class="setup-submit-spinner" v-if="isSubmitting" aria-hidden="true"></span>
                        <i class="material-icons" v-else>check_circle</i>
                        <span>{{ isSubmitting ? "Updating..." : "Update" }}</span>
                    </span>
                </button>
            </div>
        </div>
    </form>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from "vue";
import putItem from "@/composables/putItem";
import useStorage from "@/composables/useStorage";
import LoadingSmallDiv from "@/components/LoadingSmallDiv.vue";


export default {
    name: "UpdateUserComponent",
    components: { LoadingSmallDiv },
    props: ["user", "temporaryPassword"],
    emits: ["updated"],
    setup(props, context) {
        const file = ref(null);
        const inputFile = ref(null);
        const fileError = ref(null);
        const name = ref(null);
        const slug = ref(null);
        const designation = ref(null);
        const department = ref(null);
        const staff_id = ref(null);
        const official_line = ref(null);
        const password = ref(null);
        const password_confirm = ref(null);
        const email = ref(null);
        const isSubmitting = ref(false);
        const isLoading = ref(false);
        const error = ref(null);
        const img = ref(null);
        const types = ["image/jpg", "image/jpeg", "image/png", "image/gif"];
        const hasTemporaryPassword = computed(() => !!props.temporaryPassword);

        const passwordRules = computed(() => {
            const value = password.value || "";
            const confirm = password_confirm.value || "";

            return {
                hasLength: value.length >= 8,
                hasLetterOrSymbol: /[A-Za-z]|[^A-Za-z0-9]/.test(value),
                hasNumber: /\d/.test(value),
                isDifferentFromTemporary: !props.temporaryPassword || value !== props.temporaryPassword,
                confirmMatches: !!value && !!confirm && value === confirm,
            };
        });

        const shouldShowPasswordNotice = computed(() => !!password.value || !!password_confirm.value);

        const passwordPolicy = computed(() => {
            const rules = passwordRules.value;

            return {
                isValid:
                    rules.hasLength &&
                    rules.hasLetterOrSymbol &&
                    rules.hasNumber &&
                    rules.isDifferentFromTemporary &&
                    rules.confirmMatches,
            };
        });

        const getPasswordPolicyMessage = () => {
            const rules = passwordRules.value;

            if (!rules.hasLength || !rules.hasLetterOrSymbol || !rules.hasNumber) {
                return "Password must be at least 8 characters and include a number plus a letter or symbol.";
            }

            if (!rules.isDifferentFromTemporary) {
                return "Please choose a password that is different from the temporary password.";
            }

            if (!rules.confirmMatches) {
                return "Password and confirmation must match.";
            }

            return null;
        };

        // storage
        const { uploadFile, uploadProgress, uploadError, data, completed } =
            useStorage();

        // select
        onMounted(() => {
            // set other variables
            name.value = props.user.name;
            slug.value = props.user.slug;
            email.value = props.user.email;
            staff_id.value = props.user.staff_id;
            designation.value = props.user.designation;
            department.value = props.user.department;
            official_line.value = props.user.official_line;
        });

        // clear
        const clearForm = () => {
            file.value = null; //clear file value
            inputFile.value = null; // Clear the file input value
            isSubmitting.value = false;
            isLoading.value = false;
            uploadProgress.value = "0%";
        };

        // handle file
        const handleFile = (e) => {
            const selected = e.target.files[0];
            if (selected && types.includes(selected.type)) {
                file.value = selected;
                inputFile.value = file.value.name;
                fileError.value = null;
            } else {
                file.value = null;
                inputFile.value = null;
                fileError.value = "Please select an Image (.JPG, .PNG, .GIF)";
            }
        };

        //hand
        watchEffect(() => {
            if (file.value) {
                // start ploading
                isLoading.value = true;
                uploadError.value = null;

                // upload image
                uploadFile(file.value);
            }
        });

        //handle submit
        const handleUpdate = async () => {
            if (isSubmitting.value || isLoading.value) {
                return;
            }

            // Materialize CSS handled all custome errors \\

            // clear error
            error.value = null;

            if (!passwordPolicy.value.isValid) {
                error.value = getPasswordPolicyMessage();
                return;
            }

            // cont
            if (password.value === password_confirm.value) {

                // start
                isSubmitting.value = true;
                error.value = false;
                uploadProgress.value = false;

                // variable
                const newVal = {
                    name: name.value,
                    profile_img: img.value ? img.value : props.user?.profile_img,
                    email: email.value,
                    slug: slug.value,
                    staff_id: staff_id.value,
                    designation: designation.value,
                    department: department.value,
                    official_line: official_line.value,
                    password: password.value,
                    status: "a", // make active
                };

                // post item
                const { res, err } = await putItem(
                    "authority/update",
                    props.user.id,
                    newVal,
                    null,
                    props.user?.token
                );

                if (res.value?.success && res.value?.data) {
                    // go back
                    context.emit("updated", res.value.data);
                } else {
                    error.value = err.value || res.value?.message ||
                        "We could not complete the account setup. Please try again.";
                }

                // end here
                // clear
                clearForm();

            } else {

                error.value = "Password and Confirmation doesn't match!"

            }

        };

        // watch tariff upload
        watchEffect(() => {
            if (completed.value) {
                // set image
                img.value = data.value;
                // clear
                clearForm();
                // data.succ.Success, data.succ.inserted_rows, data.succ.Error
                // console.log(data); // might not be necessary
            }
            if (uploadError.value) {
                // clear
                clearForm();
            }
        });


        return {
            handleFile,
            handleUpdate,
            isSubmitting,
            isLoading,
            uploadProgress,
            fileError,
            uploadError,
            error,
            hasTemporaryPassword,
            img,
            file,
            inputFile,
            name,
            slug,
            designation,
            department,
            staff_id,
            official_line,
            email,
            password,
            password_confirm,
            passwordRules,
            shouldShowPasswordNotice,
        };
    },
};
</script>

<style scoped>
.setup-form {
    width: 100%;
}

.setup-card {
    width: 100%;
    box-sizing: border-box;
    background: #ffffff;
    border: 1px solid #dadce0;
    border-radius: 8px;
    padding: clamp(26px, 4vh, 34px);
}

.setup-header {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    margin-bottom: 22px;
}

.setup-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    flex: 0 0 56px;
    border-radius: 50%;
    background: #f8fafc;
    border: 1px solid #e8eaed;
    overflow: hidden;
}

.setup-avatar img {
    width: 56px;
    height: 56px;
    object-fit: cover;
}

.setup-logo {
    display: block;
    width: 52px;
    height: 52px;
    object-fit: contain;
    border-radius: 12px;
    margin-bottom: 14px;
}

.setup-title h5 {
    margin: 0 0 7px;
    color: #202124;
    font-size: 1.45rem;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: 0;
}

.setup-title p {
    margin: 0;
    color: #5f6368;
    font-size: 0.94rem;
}

.setup-upload {
    margin: 0 0 16px;
    padding: 12px 14px;
    background: #fafbfc;
    border: 1px solid #eef2f5;
    border-radius: 6px;
}

.setup-upload-btn {
    height: 36px;
    line-height: 36px;
    padding: 0 16px;
    background: #f1f3f4;
    color: #303f9f;
    border-radius: 4px;
    box-shadow: none;
    font-weight: 700;
}

.setup-upload .file-path-wrapper input {
    border-bottom-color: #dadce0;
}

.setup-upload p,
.setup-error {
    color: #b71c1c;
}

.setup-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 18px;
}

.input-field {
    margin-top: 10px;
    margin-bottom: 10px;
}

.input-field label {
    color: #5f6368;
}

.input-field input {
    color: #202124;
    border-bottom-color: #dadce0;
}

.input-field input:-webkit-autofill,
.input-field input:-webkit-autofill:hover,
.input-field input:-webkit-autofill:focus,
.input-field input:-webkit-autofill:active {
    -webkit-text-fill-color: #202124;
    caret-color: #202124;
    box-shadow: 0 0 0 1000px #ffffff inset;
    transition: background-color 9999s ease-in-out 0s;
}

.input-field input:focus {
    border-bottom-color: #303f9f !important;
    box-shadow: 0 1px 0 0 #303f9f !important;
}

.password-section {
    margin-top: 12px;
    padding: 16px 18px 6px;
    background: #fbfcfd;
    border: 1px solid #eef2f5;
    border-radius: 6px;
}

.password-section .setup-grid {
    grid-template-columns: 1fr;
    gap: 2px;
}

.password-section h6 {
    margin: 0 0 6px;
    color: #202124;
    font-size: 0.95rem;
    font-weight: 700;
}

.setup-notice-slide-enter-active,
.setup-notice-slide-leave-active {
    transition:
        opacity 180ms ease,
        transform 200ms ease,
        max-height 220ms ease,
        margin-top 220ms ease;
    overflow: hidden;
}

.setup-notice-slide-enter-from,
.setup-notice-slide-leave-to {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
    transform: translateY(-6px);
}

.setup-notice-slide-enter-to,
.setup-notice-slide-leave-from {
    opacity: 1;
    max-height: 210px;
    transform: translateY(0);
}

.password-guidance {
    margin-top: 6px;
    padding: 12px 13px;
    background: #ffffff;
    border: 1px solid #e8eaed;
    border-radius: 6px;
    color: #5f6368;
    font-size: 0.86rem;
}

.password-guidance p {
    margin: 0 0 8px;
    color: #405261;
    font-weight: 600;
}

.password-guidance ul {
    margin: 0;
    padding: 0;
}

.password-guidance li {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 5px 0;
    color: #6f7c86;
    line-height: 1.25;
}

.password-guidance li.met {
    color: #1f6f43;
}

.password-guidance i {
    color: #9aa0a6;
    font-size: 17px;
    line-height: 17px;
}

.password-guidance li.met i {
    color: #2e7d32;
}

.setup-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 14px;
}

.setup-error {
    display: inline-flex;
    align-items: center;
    min-height: 42px;
    padding: 0 12px;
    border-radius: 6px;
    background: #ffebee;
    border: 1px solid #ffcdd2;
    font-size: 0.9rem;
}

.setup-submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 170px;
    height: 46px;
    border-radius: 4px;
    background: #303f9f;
    font-weight: 700;
    letter-spacing: 0;
    box-shadow: none;
}

.setup-submit:hover {
    background: #283593;
}

.setup-submit-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    line-height: 1;
}

.setup-submit-content i {
    font-size: 20px;
    line-height: 20px;
}

.setup-submit-spinner {
    display: inline-block;
    width: 18px;
    height: 18px;
    box-sizing: border-box;
    border: 2px solid rgba(255, 255, 255, 0.38);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: setupButtonSpin 760ms linear infinite;
}

@keyframes setupButtonSpin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 700px) {
    .setup-card {
        padding: 28px 24px 24px;
    }

    .setup-header {
        align-items: flex-start;
        flex-direction: column;
        gap: 16px;
    }

    .setup-grid {
        grid-template-columns: 1fr;
        gap: 2px;
    }

    .setup-actions {
        align-items: stretch;
        flex-direction: column;
    }

    .setup-submit {
        width: 100%;
    }
}
</style>
